import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// All 13 Canadian provinces + territories, alphabetical.
const PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
] as const;

// Keyframes injected once; inline styles cannot define @keyframes.
const ANIMATIONS_CSS = `
  @keyframes stCloudDrift { 0% { transform: translateX(0); } 100% { transform: translateX(50px); } }
  @keyframes stIdle1 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
  @keyframes stIdle2 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
  @keyframes stIdle3 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
  @keyframes stTruckDrive {
    0%   { left: 165px; opacity: 0; }
    5%   { opacity: 1; }
    35%  { left: 380px; opacity: 1; }
    55%  { left: 380px; opacity: 1; }
    85%  { left: 640px; opacity: 1; }
    95%  { opacity: 1; }
    100% { left: 740px; opacity: 0; }
  }
  @keyframes stPaperFly {
    0%   { opacity: 0; transform: translateY(0) rotate(-5deg) scale(0.8); }
    20%  { opacity: 1; }
    80%  { opacity: 1; }
    100% { opacity: 0; transform: translateY(-80px) rotate(10deg) scale(1); }
  }
  @keyframes stSunPulse {
    0%,100% { transform: scale(1); opacity: .9; }
    50%     { transform: scale(1.1); opacity: 1; }
  }
  @keyframes stTreeSway {
    0%,100% { transform: rotate(0deg); }
    50%     { transform: rotate(2deg); }
  }
  .st-cloud  { animation: stCloudDrift 12s ease-in-out infinite alternate; }
  .st-c1     { animation-delay: 0s; animation-duration: 12s; }
  .st-c2     { animation-delay: -6s; animation-duration: 14s; }
  .st-c3     { animation-delay: -3s; animation-duration: 10s; }
  .st-shop   { animation: stIdle1 4s ease-in-out infinite; }
  .st-office { animation: stIdle2 4.5s ease-in-out infinite; animation-delay: -1.5s; }
  .st-tax    { animation: stIdle3 5s ease-in-out infinite; animation-delay: -3s; }
  .st-truck  { animation: stTruckDrive 7s ease-in-out infinite; }
  .st-paper1 { animation: stPaperFly 4s ease-in-out infinite; }
  .st-paper2 { animation: stPaperFly 4s ease-in-out infinite; animation-delay: -1.5s; }
  .st-sun    { animation: stSunPulse 4s ease-in-out infinite; }
  .st-tree   { animation: stTreeSway 5s ease-in-out infinite; transform-origin: bottom center; }
  .st-tree-2 { animation-delay: -2.5s; }
`;

// Styles
const PAGE_HEADER: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  background: '#FFFFFF',
  padding: '14px 32px',
  borderBottom: '1px solid #E7EAF0',
  flexShrink: 0,
};
const H1: React.CSSProperties = { fontSize: 20, fontWeight: 700, margin: 0, color: '#0E1A1A' };
const SUB: React.CSSProperties = { fontSize: 12.5, color: '#66748B', marginTop: 2 };

// STAGE fills the full main area (including into main's own vertical padding)
// so the sage gradient goes edge to edge with no awkward white gap below it.
// minHeight uses calc(100vh - ~180px) to account for the outer chrome + headers;
// negative vertical margins swallow main's padding ('28px 32px 90px') so the
// box visually reaches the top and bottom of the content card.
const STAGE: React.CSSProperties = {
  minHeight: 'calc(100vh - 180px)',
  margin: '-28px 0 -90px',
  background: 'linear-gradient(180deg, #F0F9FF 0%, #E0F2FE 50%, #F0FDF4 100%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px 50px',
  position: 'relative',
};
// Overflow lives on the inner scene (not the stage) so the parent main can
// still scroll vertically when the viewport is shorter than STAGE's minHeight.
const SCENE: React.CSSProperties = { position: 'relative', width: 860, height: 340, maxWidth: '100%', overflow: 'hidden' };
const HEADLINE: React.CSSProperties = { fontSize: 26, fontWeight: 800, color: '#0E1A1A', margin: '30px 0 10px', textAlign: 'center' };
const LEAD: React.CSSProperties = { fontSize: 14.5, color: '#66748B', textAlign: 'center', maxWidth: 520, margin: '0 0 22px', lineHeight: 1.5 };
const CTA: React.CSSProperties = {
  background: '#0F6E56',
  color: '#FFFFFF',
  border: 0,
  padding: '14px 32px',
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 4px 14px rgba(15,110,86,0.3)',
};
const TIP_ROW: React.CSSProperties = {
  marginTop: 24,
  fontSize: 12.5,
  color: '#66748B',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};
const TIP_LINK: React.CSSProperties = { color: '#15A08C', textDecoration: 'none', fontWeight: 600, cursor: 'pointer' };

// Modal styles
const OVERLAY: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(14,26,26,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const MODAL: React.CSSProperties = {
  background: '#FFFFFF',
  borderRadius: 14,
  padding: '28px 30px 24px',
  width: 460,
  maxWidth: 'calc(100vw - 32px)',
  position: 'relative',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const MODAL_CLOSE: React.CSSProperties = {
  position: 'absolute',
  top: 16,
  right: 18,
  background: 'none',
  border: 0,
  cursor: 'pointer',
  color: '#66748B',
  fontSize: 22,
  lineHeight: 1,
};
const MODAL_H: React.CSSProperties = { fontSize: 19, fontWeight: 800, margin: '0 0 14px', color: '#0E1A1A' };
const MODAL_P: React.CSSProperties = { fontSize: 13.5, color: '#66748B', margin: '0 0 18px' };
const SELECT_LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#0E1A1A', display: 'block', marginBottom: 6 };
const SELECT: React.CSSProperties = {
  background: '#FFFFFF',
  border: '1.5px solid #15A08C',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 13.5,
  color: '#0E1A1A',
  width: '100%',
  cursor: 'pointer',
  outline: 'none',
};
const SAVE_ROW: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', marginTop: 20 };
const SAVE_BTN: React.CSSProperties = {
  background: '#0F6E56',
  color: '#FFFFFF',
  border: 0,
  padding: '10px 22px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
};
const SAVE_BTN_DISABLED: React.CSSProperties = { ...SAVE_BTN, background: '#94A3B8', cursor: 'not-allowed' };

/**
 * Page header rendered OUTSIDE main via AppLayout's `pageHeader` prop.
 */
export function SalesTaxOverviewHeader(): React.ReactElement {
  return (
    <div style={PAGE_HEADER}>
      <div>
        <h1 style={H1}>Sales tax</h1>
        <div style={SUB}>Track and file what you owe.</div>
      </div>
    </div>
  );
}

export default function SalesTaxOverview(): React.ReactElement {
  const [modalOpen, setModalOpen] = useState(false);
  const [province, setProvince] = useState('');
  const navigate = useNavigate();

  const handleSave = () => {
    if (!province) return;
    // Persist for the session; backend wiring comes in a follow-up PR.
    try {
      localStorage.setItem('novala_sales_tax_province', province);
    } catch {}
    setModalOpen(false);
    // Hand off to the existing sales tax calculator while the dashboard is built.
    navigate('/tax/calculator');
  };

  return (
    <>
      <style>{ANIMATIONS_CSS}</style>

      <div style={STAGE}>
        <div style={SCENE}>
          {/* Clouds */}
          <svg className="st-cloud st-c1" style={{ position: 'absolute', top: 10, left: 60, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,.05))' }} width="80" height="36" viewBox="0 0 80 36">
            <ellipse cx="20" cy="22" rx="18" ry="12" fill="#fff" />
            <ellipse cx="40" cy="18" rx="22" ry="14" fill="#fff" />
            <ellipse cx="60" cy="22" rx="16" ry="11" fill="#fff" />
          </svg>
          <svg className="st-cloud st-c2" style={{ position: 'absolute', top: 40, right: 120 }} width="70" height="32" viewBox="0 0 70 32">
            <ellipse cx="18" cy="20" rx="15" ry="10" fill="#fff" />
            <ellipse cx="35" cy="16" rx="18" ry="12" fill="#fff" />
            <ellipse cx="52" cy="20" rx="14" ry="10" fill="#fff" />
          </svg>
          <svg className="st-cloud st-c3" style={{ position: 'absolute', top: 20, left: '50%' }} width="60" height="28" viewBox="0 0 60 28">
            <ellipse cx="15" cy="18" rx="13" ry="9" fill="#fff" />
            <ellipse cx="30" cy="14" rx="15" ry="10" fill="#fff" />
            <ellipse cx="45" cy="18" rx="12" ry="9" fill="#fff" />
          </svg>

          {/* Sun */}
          <svg className="st-sun" style={{ position: 'absolute', top: 20, right: 40 }} width="50" height="50" viewBox="0 0 50 50">
            <circle cx="25" cy="25" r="14" fill="#FBBF24" />
            <g stroke="#F59E0B" strokeWidth={2.5} strokeLinecap="round">
              <line x1="25" y1="2" x2="25" y2="8" />
              <line x1="25" y1="42" x2="25" y2="48" />
              <line x1="2" y1="25" x2="8" y2="25" />
              <line x1="42" y1="25" x2="48" y2="25" />
              <line x1="9" y1="9" x2="13" y2="13" />
              <line x1="37" y1="37" x2="41" y2="41" />
              <line x1="9" y1="41" x2="13" y2="37" />
              <line x1="37" y1="13" x2="41" y2="9" />
            </g>
          </svg>

          {/* SHOP */}
          <svg className="st-shop" style={{ position: 'absolute', left: 0, top: 120 }} width="200" height="200" viewBox="0 0 200 200">
            <ellipse cx="100" cy="190" rx="80" ry="8" fill="rgba(0,0,0,0.15)" />
            <polygon points="130,80 180,110 180,180 130,180" fill="#D97706" />
            <polygon points="20,80 130,80 130,180 20,180" fill="#F59E0B" />
            <polygon points="20,80 130,80 180,110 70,110" fill="#B45309" />
            <polygon points="20,105 130,105 130,120 20,120" fill="#DC2626" />
            <polygon points="20,120 130,120 125,135 25,135" fill="#F59E0B" />
            <rect x="40" y="140" width="30" height="40" fill="#78350F" />
            <circle cx="63" cy="162" r="2" fill="#FBBF24" />
            <rect x="85" y="140" width="35" height="30" fill="#E0F2FE" stroke="#78350F" strokeWidth={2} />
            <text x="102" y="162" textAnchor="middle" fill="#15A08C" fontSize="20" fontWeight="800">$</text>
            <rect x="35" y="85" width="80" height="15" fill="#15A08C" rx="2" />
            <text x="75" y="97" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="800">SHOP</text>
          </svg>

          {/* OFFICE */}
          <svg className="st-office" style={{ position: 'absolute', left: '50%', top: 85, transform: 'translateX(-50%)' }} width="200" height="220" viewBox="0 0 200 220">
            <ellipse cx="100" cy="210" rx="80" ry="8" fill="rgba(0,0,0,0.15)" />
            <polygon points="130,70 180,100 180,200 130,200" fill="#0F6E56" />
            <polygon points="20,70 130,70 130,200 20,200" fill="#15A08C" />
            <polygon points="20,70 130,70 180,100 70,100" fill="#0A4F3F" />
            <g fill="#E0F2FE" stroke="#0A4F3F" strokeWidth={1.5}>
              <rect x="35" y="90" width="22" height="22" />
              <rect x="65" y="90" width="22" height="22" />
              <rect x="95" y="90" width="22" height="22" />
              <rect x="35" y="120" width="22" height="22" />
              <rect x="65" y="120" width="22" height="22" />
              <rect x="95" y="120" width="22" height="22" />
              <rect x="35" y="150" width="22" height="22" />
              <rect x="65" y="150" width="22" height="22" />
              <rect x="95" y="150" width="22" height="22" />
            </g>
            <rect x="55" y="175" width="40" height="25" fill="#0A4F3F" />
            <circle cx="88" cy="188" r="2" fill="#FBBF24" />
            <rect x="30" y="78" width="90" height="10" fill="#F59E0B" rx="1" />
            <text x="75" y="86" textAnchor="middle" fill="#78350F" fontSize="7" fontWeight="800">ACCOUNTANT</text>
          </svg>

          {/* Floating papers */}
          <div className="st-paper1" style={{ position: 'absolute', top: 110, left: '50%', marginLeft: -15, width: 30, height: 36, background: '#fff', border: '1.5px solid #0F6E56', borderRadius: 3, zIndex: 4 }}>
            <div style={{ position: 'absolute', top: 7, left: 4, right: 4, height: 2, background: '#15A08C', borderRadius: 1 }} />
            <div style={{ position: 'absolute', top: 14, left: 4, right: 12, height: 2, background: '#15A08C', borderRadius: 1 }} />
          </div>
          <div className="st-paper2" style={{ position: 'absolute', top: 110, left: 'calc(50% + 10px)', marginLeft: -15, width: 30, height: 36, background: '#fff', border: '1.5px solid #0F6E56', borderRadius: 3, zIndex: 4 }}>
            <div style={{ position: 'absolute', top: 7, left: 4, right: 4, height: 2, background: '#15A08C', borderRadius: 1 }} />
            <div style={{ position: 'absolute', top: 14, left: 4, right: 12, height: 2, background: '#15A08C', borderRadius: 1 }} />
          </div>

          {/* TAX OFFICE */}
          <svg className="st-tax" style={{ position: 'absolute', right: 0, top: 95 }} width="220" height="210" viewBox="0 0 220 210">
            <ellipse cx="110" cy="200" rx="90" ry="8" fill="rgba(0,0,0,0.15)" />
            <polygon points="20,180 200,180 180,195 40,195" fill="#78716C" />
            <polygon points="150,90 200,120 200,180 150,180" fill="#A8A29E" />
            <polygon points="20,90 150,90 150,180 20,180" fill="#D6D3D1" />
            <g fill="#F5F5F4">
              <rect x="28" y="105" width="12" height="75" />
              <rect x="50" y="105" width="12" height="75" />
              <rect x="72" y="105" width="12" height="75" />
              <rect x="94" y="105" width="12" height="75" />
              <rect x="116" y="105" width="12" height="75" />
              <rect x="138" y="105" width="12" height="75" />
            </g>
            <polygon points="20,90 150,90 200,120 70,120" fill="#44403C" />
            <polygon points="85,40 150,90 20,90" fill="#D6D3D1" />
            <polygon points="85,40 150,90 200,120 150,90" fill="#A8A29E" />
            <line x1="90" y1="40" x2="90" y2="15" stroke="#44403C" strokeWidth={2} />
            <polygon points="90,15 110,20 90,25" fill="#DC2626" />
            <rect x="55" y="98" width="60" height="10" fill="#44403C" rx="1" />
            <text x="85" y="106" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="800">TAX OFFICE</text>
          </svg>

          {/* Trees */}
          <svg className="st-tree" style={{ position: 'absolute', bottom: 60, left: 200 }} width="40" height="70" viewBox="0 0 40 70">
            <rect x="18" y="40" width="6" height="30" fill="#78350F" />
            <ellipse cx="20" cy="30" rx="18" ry="22" fill="#059669" />
            <ellipse cx="12" cy="22" rx="10" ry="12" fill="#10B981" />
            <ellipse cx="28" cy="22" rx="10" ry="12" fill="#10B981" />
          </svg>
          <svg className="st-tree st-tree-2" style={{ position: 'absolute', bottom: 70, right: 230 }} width="40" height="70" viewBox="0 0 40 70">
            <rect x="18" y="40" width="6" height="30" fill="#78350F" />
            <ellipse cx="20" cy="30" rx="18" ry="22" fill="#059669" />
            <ellipse cx="12" cy="22" rx="10" ry="12" fill="#10B981" />
            <ellipse cx="28" cy="22" rx="10" ry="12" fill="#10B981" />
          </svg>

          {/* Truck */}
          <svg className="st-truck" style={{ position: 'absolute', top: 245, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,.15))', zIndex: 5 }} width="80" height="50" viewBox="0 0 80 50">
            <rect x="2" y="18" width="48" height="22" fill="#15A08C" rx="2" />
            <polygon points="50,18 70,18 76,28 50,28" fill="#0F6E56" />
            <polygon points="54,20 68,20 72,27 54,27" fill="#E0F2FE" />
            <circle cx="14" cy="42" r="6" fill="#0F172A" />
            <circle cx="14" cy="42" r="2.5" fill="#64748B" />
            <circle cx="60" cy="42" r="6" fill="#0F172A" />
            <circle cx="60" cy="42" r="2.5" fill="#64748B" />
            <circle cx="26" cy="29" r="7" fill="#fff" />
            <text x="26" y="33" textAnchor="middle" fill="#F59E0B" fontSize="12" fontWeight="800">$</text>
          </svg>
        </div>

        <h2 style={HEADLINE}>Let's set you up to collect and track sales tax</h2>
        <p style={LEAD}>Collect at the shop, calculate with your accountant, file with the tax office. We handle every step so you stay compliant.</p>
        <button type="button" style={CTA} onClick={() => setModalOpen(true)}>Set up sales tax</button>

        <div style={TIP_ROW}>
          <span>💡</span>
          Tip: New to sales tax? <a style={TIP_LINK}>Here's what you need to know</a>
        </div>
      </div>

      {modalOpen && (
        <div style={OVERLAY} onClick={() => setModalOpen(false)}>
          <div style={MODAL} onClick={(e) => e.stopPropagation()}>
            <button type="button" style={MODAL_CLOSE} onClick={() => setModalOpen(false)}>×</button>
            <h3 style={MODAL_H}>Set up sales tax</h3>
            <p style={MODAL_P}>To get started, tell us where your business is based:</p>
            <label style={SELECT_LABEL} htmlFor="province">Province or territory</label>
            <select
              id="province"
              style={SELECT}
              value={province}
              onChange={(e) => setProvince(e.target.value)}
            >
              <option value="">Choose a province or territory</option>
              {PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <div style={SAVE_ROW}>
              <button
                type="button"
                style={province ? SAVE_BTN : SAVE_BTN_DISABLED}
                onClick={handleSave}
                disabled={!province}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
